import React, { useEffect, useState, useCallback } from 'react';
import { History, Download, Mail, Loader2 } from 'lucide-react';
import api from '../../lib/api';

/**
 * A compact "Certificate History" strip for the Employee Detail > Salary
 * tab. Shows the last N salary-certificate issuances (both downloads and
 * emails) with FY, annual gross, recipient (for emails) and timestamp.
 *
 * Refetches whenever `refreshToken` changes — parent bumps it after each
 * successful download / email so the strip reflects the new row without
 * a full page reload.
 */
const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export function SalaryCertificateHistory({ employeeId, refreshToken, limit = 3 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/payroll/employees/${employeeId}/salary-certificate/history`,
        { params: { limit } },
      );
      setRows(res.data?.data || []);
    } catch (err) {
      // 403 (no perm) is a valid state — silently hide the strip in that case
      if (err?.response?.status === 403) {
        setRows([]);
      } else {
        setError(err?.response?.data?.detail || 'Failed to load history');
      }
    } finally {
      setLoading(false);
    }
  }, [employeeId, limit]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Hide the strip entirely when there's nothing to show and we're not
  // in a loading/error state — keeps the tab clean for fresh employees.
  if (!loading && !error && rows.length === 0) return null;

  return (
    <div
      className="rounded-md border border-border bg-muted/30 px-4 py-3"
      data-testid="salary-cert-history"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <History className="h-4 w-4 text-muted-foreground" />
          Certificate History
          <span className="text-xs text-muted-foreground font-normal">
            (last {limit})
          </span>
        </div>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid="salary-cert-history-error">
          {error}
        </p>
      )}
      {!error && rows.length > 0 && (
        <ul className="space-y-1.5 text-xs">
          {rows.map((r, i) => (
            <li
              key={i}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
              data-testid="salary-cert-history-row"
            >
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md font-medium ${
                  r.action === 'emailed'
                    ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                }`}
              >
                {r.action === 'emailed'
                  ? <Mail className="h-3 w-3" />
                  : <Download className="h-3 w-3" />}
                {r.action === 'emailed' ? 'Emailed' : 'Downloaded'}
              </span>
              <span className="font-medium text-foreground">FY {r.fy}</span>
              <span className="text-muted-foreground">
                Rs. {fmtINR(r.annual_gross)}
              </span>
              {r.recipient && (
                <span className="text-muted-foreground truncate max-w-[220px]">
                  → {r.recipient}
                </span>
              )}
              <span className="text-muted-foreground ml-auto">
                {fmtWhen(r.sent_at)}
              </span>
              {r.triggered_by && (
                <span className="text-muted-foreground/70 text-[10px]">
                  by {r.triggered_by}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
